/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ReactFlowProvider, addEdge, useEdgesState, useNodesState, useReactFlow, type Edge } from '@xyflow/react'
import { GitForkIcon } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'urql'
import { Button } from 'app/components/ui/button'
import type { Workflow, WorkflowRunEvent } from 'app/generated/workflows'
import { ObjectId } from 'lib/ObjectId'
import { notify, notifySuccess } from 'app/services/notify.client'
import { useListCopy } from 'app/hooks/useListCopy'
import { useUser, useWorkspace } from 'app/utils'
import { useAuthStore, useWorkspaceStore } from 'app/stores/StoreContext'
import { logError } from 'lib/logging'
import { ActivationPreflight, type GoLiveEnrollment } from './ActivationPreflight'
import { GlobalSettingsModal } from './GlobalSettingsModal'
import { workspaceInputFromSettings } from 'app/lib/workflows/globalSettings'
import { useWorkspaceThrottle } from './useWorkspaceThrottle'
import { useSmsConsentGate } from './sms-consent/useSmsConsentGate'
import { SmsConsentBar } from './sms-consent/SmsConsentBar'
import { workflowDocFromRemote } from 'app/lib/workflows/deserialize'
import {
  GET_WORKFLOW_QUERY,
  WORKFLOW_LOCATIONS_QUERY,
  WORKFLOW_RUN_EVENTS_QUERY,
  WORKFLOW_STOPPED_QUERY,
} from 'app/lib/workflows/graphql'
import { CHAIN_NODE_SUFFIX } from 'app/lib/workflows/stepChecks'
import { SMS_AGENT_READY_QUERY } from 'app/graphql/agentThreads'
import { OPS_URL } from 'app/lib/opsUrqlClient'
import { isSystemEnrolmentWorkflow } from 'app/lib/workflows/systemEnrolment'
import { RunStatusContext, deriveRunNodeStatus, hasEnrollment } from './runViz'
import { layoutFlow } from 'app/lib/workflows/autoLayout'
import { BlockPicker } from './BlockPicker'
import type { BackfillControl } from './inspectors/TriggerPanel'
import { MatchesPanel } from './MatchesPanel'
import { NodeInspector } from './NodeInspector'
import { TriggerPicker, type TriggerChoice } from './inspectors/TriggerPicker'
import { WorkflowSettingsPanel } from './inspectors/WorkflowSettingsPanel'
import { RunsPanel } from './RunsPanel'
import { WorkflowActionsProvider, type AppendTarget, type InsertEdgeTarget } from './WorkflowActions'
import { WorkflowCanvas } from './WorkflowCanvas'
import { useTriggerLookback } from './useTriggerLookback'
import { WorkflowMatches } from './WorkflowMatches'
import { WorkflowToolbar } from './WorkflowToolbar'
import { WorkflowStoppedBanner } from './WorkflowStoppedBanner'
import { TASK_PATH_HANDLE, bridgeEdges, isOnTaskPath } from 'app/lib/workflows/graphUtils'
import { duplicateEdges, moveNode, moveScope } from 'app/lib/workflows/graphEdits'
import type { MoveDrop } from 'app/types/workflow-graph-edits'
import { useEscapeListener } from 'app/hooks/useEscapeListener'
import {
  deriveActivityAvailability,
  deriveAvailableFields,
  deriveContactAvailability,
} from 'app/lib/workflows/dataContext'
import { computeNodeIssues, nodeIssuesFor, totalIssueCount } from 'app/lib/workflows/nodeIssues'
import { useUndoRedo } from './useUndoRedo'
import { usePreviewSample } from './usePreviewSample'
import { engineEventNoun, engineEventSource, triggerDef, triggerLabel } from 'app/lib/workflows/triggerCatalog'
import { type TriggerSpec } from './WhyDidntItFire'
import { isDateAnchored, serializeWorkflow } from 'app/lib/workflows/serialize'
import { pendingEngineGaps } from 'app/lib/workflows/pendingEngineSupport'
import { isSmsConsentRefusal, sendsSms } from 'app/lib/workflows/smsConsent'
import { type LocationBusinessHours, locationHasBusinessHours } from 'app/lib/workflows/validation'
import { createRemoteWorkflow, setRemoteWorkflowCategory, updateRemoteWorkflow } from 'app/lib/workflows/workflowApi'
import { DEFAULT_WORKFLOW_SETTINGS, categorySyncPlan, effectiveCategory } from 'app/lib/workflows/workflowSettings'
import { workflowTemplateById } from 'app/lib/workflows/workflowTemplates'
import { adoptServerId, loadWorkflow, saveWorkflow } from 'app/lib/workflows/workflowStore'
import type { PaletteItem } from 'app/lib/workflows/palette'
import type {
  MessageCategory,
  SendTimesSettings,
  TriggerFlowNode,
  AddToPipelineFlowNode,
  WorkflowNode,
  WorkflowSettings,
} from 'app/types/workflows'

/** When set, the block picker is open; onPick performs the pending insert/connect. */
/** `onTaskPath` says whether the new step lands on a Create task's task path, the only place Edit
 *  task has a task to edit. */
type PickerState = { screen: { x: number; y: number }; onPick: (item: PaletteItem) => void; onTaskPath: boolean }

const NODE_HALF_WIDTH = 120
const GRID = 20
/** How far to push downstream nodes down when inserting a step between two nodes. */
const INSERT_SHIFT = 160

const snap = (value: number) => Math.round(value / GRID) * GRID

/** Stable empty map so the run-status context value doesn't churn each render when not watching a run. */
const EMPTY_RUN_STATUS = new Map<string, string>()

/** The API clamps a page here. The picker needs every location, not a first page: orgs run past 100. */
const LOCATION_PAGE_LIMIT = 1000

/** All nodes reachable downstream of (and including) startId, following outgoing edges. */
function collectDownstream(startId: string, edges: Edge[]): Set<string> {
  const visited = new Set<string>()
  const stack = [startId]
  while (stack.length > 0) {
    const id = stack.pop()
    if (id === undefined || visited.has(id)) continue
    visited.add(id)
    for (const edge of edges) if (edge.source === id) stack.push(edge.target)
  }
  return visited
}

type WorkflowBuilderPageProps = {
  mode: `new` | `edit`
  workflowName?: string
  workflowId?: string
  /** Starter template to seed a new workflow from (see workflowTemplates.ts). */
  templateId?: string
}

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  const tag = el?.tagName
  return tag === `INPUT` || tag === `TEXTAREA` || tag === `SELECT` || el?.isContentEditable === true
}

/** Stable signature of the MEANINGFUL workflow state (name, node data/structure, edges, settings) —
 *  excludes selection and positions, which are transient/auto-derived. Used to detect unsaved
 *  changes: the engine runs the last SAVED version, so a dirty builder means a test would run stale
 *  config. `active` is deliberately NOT in here: on/off persists the instant it is flipped, so it is
 *  tracked against its own saved value (see savedActive). */
function workflowSignature(
  name: string,
  triggerStartDate: string,
  backfill: boolean,
  nodes: WorkflowNode[],
  edges: Edge[],
  settings: WorkflowSettings,
): string {
  return JSON.stringify({
    name,
    triggerStartDate,
    backfill,
    nodes: nodes.map((n) => ({ id: n.id, type: n.type, data: n.data })),
    edges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle ?? null })),
    settings,
  })
}

// observer: reads authStore.isSuper in render (canSetCategory gating). isSuper derives
// from authStore.user, which is reassigned in place on a workspace switch, so without
// observer the category permission would not react (TRK-6020).
const WorkflowBuilderInner = observer(function WorkflowBuilderInner({
  mode,
  workflowName,
  workflowId,
  templateId,
}: WorkflowBuilderPageProps) {
  const navigate = useNavigate()
  const user = useUser()
  const userId = user?.id
  const workspaceId = useWorkspace()?.id
  const copy = useListCopy()
  // A workflow the server holds opens from the server, so everyone who opens it sees the same
  // canvas. The local copy wins only while the server has never seen it, or when the last save
  // didn't reach it. Otherwise the canvas starts empty and the effect below hydrates it.
  const isNew = mode === `new`
  const saved = useMemo(() => {
    const local = mode === `edit` && workflowId ? loadWorkflow(userId, workflowId) : undefined
    return local && (!local.remoteId || local.unsynced) ? local : undefined
  }, [mode, workflowId, userId])
  // A starter template seeds a fresh doc (new ids per build) — the canvas opens pre-built and
  // UNSAVED (savedSig below is computed from the empty state, so the save bar prompts immediately).
  const template = isNew ? workflowTemplateById(templateId, copy)?.build?.() : undefined
  const [nodes, setNodes, onNodesChange] = useNodesState<WorkflowNode>(saved?.nodes ?? template?.nodes ?? [])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(saved?.edges ?? template?.edges ?? [])
  const [name, setName] = useState(saved?.name ?? template?.name ?? workflowName ?? `Untitled workflow`)
  const [triggerStartDate, setTriggerStartDate] = useState(saved?.triggerStartDate ?? new Date().toISOString())
  // New workflows start without history; saved ones already backfill from their date.
  const [backfill, setBackfill] = useState(saved ? (saved.backfill ?? true) : false)
  // The start the engine holds, so switching backfill off on a live workflow never replays history.
  const savedStart = useRef(saved?.triggerStartDate ?? ``)
  // A workflow belongs to one location (trackable-api #530); with exactly one, the effect below
  // picks it so the toolbar stays quiet.
  const [locationId, setLocationId] = useState<string | undefined>(saved?.locationId)
  const [{ data: locationData }] = useQuery({
    query: WORKFLOW_LOCATIONS_QUERY,
    variables: { pagination: { limit: LOCATION_PAGE_LIMIT } },
  })
  const locations = useMemo(
    () => (locationData?.locations?.hits ?? []) as ({ id: string; name?: string | null } & LocationBusinessHours)[],
    [locationData],
  )
  // Whether a Wait can actually hold for business hours here. Only a loaded location with hours
  // saved counts, so the caveat stays up while the query is in flight or no location is chosen.
  const businessHoursReady = useMemo(
    () => locationHasBusinessHours(locations.find((location) => location.id === locationId)),
    [locations, locationId],
  )
  useEffect(() => {
    if (!locationId && locations.length === 1) setLocationId(locations[0].id)
  }, [locationId, locations])
  // The same readiness the engine gates the agent handoff on. It lives on the ops schema.
  const opsContext = useMemo(() => ({ url: OPS_URL }), [])
  const [{ data: agentReadyData }] = useQuery({
    query: SMS_AGENT_READY_QUERY,
    variables: { locationId: locationId! },
    pause: !locationId,
    context: opsContext,
  })
  const smsAgentReady = agentReadyData?.smsAgentReady ?? undefined
  const [active, setActive] = useState(saved?.active ?? false)
  const [paused, setPaused] = useState(false)
  const [pausing, setPausing] = useState(false)
  const [wentLive, setWentLive] = useState(saved?.wentLive ?? saved?.active ?? false)
  // The backend workflow id, once synced. Undefined for local-only drafts (unsupported nodes).
  const [remoteId, setRemoteId] = useState<string | undefined>(saved?.remoteId)
  // updateWorkflow REJECTS the whole update when locationId is present on an ACTIVE workflow, so
  // saves must omit it unless it really changed. Tracks what the server last accepted.
  const syncedLocationId = useRef<string | undefined>(saved?.remoteId ? saved?.locationId : undefined)
  // The engine-side category (api #581/#588: marketing unless an admin says transactional). Tracked
  // so a super's save only calls the ops mutation when the picker actually disagrees with the server.
  const syncedCategory = useRef<string | null | undefined>(undefined)
  const authStore = useAuthStore()
  const [picker, setPicker] = useState<PickerState | null>(null)
  // The trigger picker lives in the inspector (design language: one frame per decision). A new
  // blank workflow opens with it; a ?template= deep link seeds directly and skips it.
  const [pickingTrigger, setPickingTrigger] = useState(isNew && !template)
  // The workflow's texting policy (send window, category) is edited in its own inspector panel,
  // reached from any SMS step's read-back line. Closing it returns to the step.
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [runsOpen, setRunsOpen] = useState(false)
  const [matchesOpen, setMatchesOpen] = useState(false)
  const [backfillListOpen, setBackfillListOpen] = useState(false)
  const [watchedRunId, setWatchedRunId] = useState<string | null>(null)
  // Flipping Draft → Active reviews before anything goes live.
  const [preflightOpen, setPreflightOpen] = useState(false)
  const [capsOpen, setCapsOpen] = useState(false)
  const workspaceStore = useWorkspaceStore()
  const throttle = useWorkspaceThrottle()
  const {
    ensure: ensureSmsConsent,
    runGated: runSmsGated,
    modal: smsConsentModal,
    collapsed: smsConsentCollapsed,
  } = useSmsConsentGate(workspaceId)
  // Bumped when someone tries to turn the workflow on past the collapsed consent bar: the bar
  // wiggles instead of the switch moving.
  const [consentNudge, setConsentNudge] = useState(0)
  const [settings, setSettings] = useState<WorkflowSettings>(
    // A template's texting category is part of its design (marketing = consent-gated); a blank
    // build keeps the DEFAULT's safe `marketing`.
    saved?.settings ??
      (template ? { ...DEFAULT_WORKFLOW_SETTINGS, category: template.messageCategory } : DEFAULT_WORKFLOW_SETTINGS),
  )
  // Signature of the last-saved state; `dirty` is the drift from it. The trigger test runs the
  // SAVED version, so testing while dirty would exercise stale config.
  const [savedSig, setSavedSig] = useState(() =>
    workflowSignature(
      saved?.name ?? workflowName ?? `Untitled workflow`,
      saved?.triggerStartDate ?? ``,
      saved ? (saved.backfill ?? true) : false,
      saved?.nodes ?? [],
      saved?.edges ?? [],
      saved?.settings ?? DEFAULT_WORKFLOW_SETTINGS,
    ),
  )
  // On/off is persisted the moment it is flipped, not on Save, so it carries its own saved value.
  const [savedActive, setSavedActive] = useState(saved?.active ?? false)
  const { screenToFlowPosition, fitView } = useReactFlow()
  const { takeSnapshot, undo, redo, canUndo, canRedo } = useUndoRedo({ nodes, edges, setNodes, setEdges })
  // Read the latest edges inside the structure-keyed layout effect without making it a dep (which would
  // re-flow on every position tweak and fight itself).
  const edgesRef = useRef(edges)
  edgesRef.current = edges

  // Hydrates the canvas once. Network-only because the canvas hydrates from the first answer: a
  // cached one would be whatever this tab saw last, not what someone else saved since.
  const needRemote = mode === `edit` && !!workflowId && !saved
  const [remoteResult] = useQuery<{ workflow: Workflow | undefined }>({
    query: GET_WORKFLOW_QUERY,
    variables: { id: workflowId },
    pause: !needRemote,
    requestPolicy: `network-only`,
  })
  const hydrated = useRef(false)
  useEffect(() => {
    if (!needRemote || hydrated.current) return
    const remote = remoteResult.data?.workflow
    if (!remote) return
    // An Action Center enrolment workflow is ours. The list hides them from a
    // practice; this closes the direct-URL door, which is the one a bookmark
    // or a shared link opens.
    if (isSystemEnrolmentWorkflow(remote) && user?.type !== `super`) {
      navigate(`/workflows`, { replace: true })
      return
    }
    hydrated.current = true
    const doc = workflowDocFromRemote(remote, copy)
    setNodes(doc.nodes)
    setEdges(doc.edges)
    setName(doc.name)
    setTriggerStartDate(doc.triggerStartDate)
    savedStart.current = doc.triggerStartDate
    setBackfill(doc.backfill ?? true)
    setActive(doc.active)
    setPaused(remote.paused ?? false)
    setWentLive(doc.wentLive ?? doc.active)
    setRemoteId(doc.remoteId)
    if (doc.locationId) setLocationId(doc.locationId)
    syncedLocationId.current = doc.locationId
    syncedCategory.current = remote.category ?? null
    setSettings(doc.settings)
    setSavedActive(doc.active)
    setSavedSig(
      workflowSignature(doc.name, doc.triggerStartDate, doc.backfill ?? true, doc.nodes, doc.edges, doc.settings),
    )
  }, [needRemote, remoteResult.data, setNodes, setEdges, user, navigate, copy])
  // The URL names a workflow nobody has: no local copy, and the server answered without one. That
  // used to render a blank "Untitled workflow" canvas, which reads as data loss and invites a save
  // that creates a duplicate instead of editing the original (TRK-5696). The answered check matters:
  // before the first response both `data` and `error` are undefined, and treating that as "missing"
  // would flash this panel over every workflow opened from a link.
  const answered = !!remoteResult.data || !!remoteResult.error
  const notFound = needRemote && answered && !remoteResult.fetching && !remoteResult.data?.workflow

  const selectedNode = nodes.find((node) => node.selected) ?? null
  // Picking a step means looking at it; an open settings panel would hide it.
  const selectedId = selectedNode?.id
  useEffect(() => {
    if (selectedId) setSettingsOpen(false)
  }, [selectedId])
  const availableFields = useMemo(() => deriveAvailableFields(nodes), [nodes])
  const contact = useMemo(() => deriveContactAvailability(nodes), [nodes])
  const activity = useMemo(() => deriveActivityAvailability(nodes), [nodes])
  // One shared "test record" the whole builder previews against.
  const triggerData = useMemo(
    () => nodes.find((node): node is TriggerFlowNode => node.type === `trigger`)?.data,
    [nodes],
  )
  const preview = usePreviewSample(triggerData)
  // Only a date trigger can say who it will reach next: an event trigger's people have not acted yet.
  const matchesAvailable = !!triggerData && isDateAnchored(triggerData.kind)
  const dirty = useMemo(
    () =>
      active !== savedActive ||
      workflowSignature(name, triggerStartDate, backfill, nodes, edges, settings) !== savedSig,
    [name, triggerStartDate, backfill, active, savedActive, nodes, edges, settings, savedSig],
  )
  const { pending: categoryPending } = effectiveCategory({
    canSetCategory: authStore.isSuper,
    settings,
    serverCategory: syncedCategory.current,
  })
  // One issue computation for card badges, inspector and toolbar count, so all three agree.
  const issues = useMemo(
    () =>
      computeNodeIssues(
        nodes,
        edges,
        { availableFields, contact, activity, smsAgentReady, triggerKind: triggerData?.kind },
        copy,
        settings,
        locationId,
        businessHoursReady,
        categoryPending,
      ),
    [
      nodes,
      edges,
      availableFields,
      contact,
      activity,
      smsAgentReady,
      triggerData?.kind,
      copy,
      settings,
      locationId,
      businessHoursReady,
      categoryPending,
    ],
  )
  const issueCount = useMemo(() => totalIssueCount(issues, nodes), [issues, nodes])
  // The consent modal, closed without confirming, stays on the canvas as a bar for as long as it
  // still applies: removing every texting step makes it go away.
  const showConsentBar = smsConsentCollapsed && !savedActive && sendsSms(nodes)
  // Non-blocking "the engine won't run this exactly as drawn" notes, surfaced in the toolbar.
  const wontRunYet = useMemo(() => {
    const notes: string[] = []
    for (const { warnings } of issues.values()) if (warnings.engineGap) notes.push(warnings.engineGap)
    return notes
  }, [issues])

  // While a run is selected, poll its events and light up each node as the run reaches it.
  const [runEventsResult, reexecuteRunEvents] = useQuery<{ workflowRunEvents: { hits: WorkflowRunEvent[] } }>({
    query: WORKFLOW_RUN_EVENTS_QUERY,
    variables: {
      search: `runId:${watchedRunId}`,
      pagination: { limit: 200 },
      sort: { field: `timestamp`, direction: `ASC` },
    },
    pause: !watchedRunId,
    requestPolicy: `cache-and-network`,
  })
  useEffect(() => {
    if (!watchedRunId) return
    const timer = window.setInterval(() => reexecuteRunEvents({ requestPolicy: `network-only` }), 2500)
    return () => window.clearInterval(timer)
  }, [watchedRunId, reexecuteRunEvents])
  const [stoppedResult] = useQuery({
    query: WORKFLOW_STOPPED_QUERY,
    variables: {
      id: remoteId ?? ``,
      heldSearch: `workflowId:${remoteId} AND status:(paused OR waiting OR running)`,
    },
    pause: !remoteId,
    requestPolicy: `network-only`,
  })
  const stopped = stoppedResult.data?.workflow?.paused ? stoppedResult.data.workflow : undefined
  // A hold pinned to a node is the engine's: a step broke. One without a node is a staff pause.
  const hold = stopped ? (stopped.pausedNodeId ? (`error` as const) : (`paused` as const)) : undefined
  const stoppedNodeId = hold === `error` ? stopped?.pausedNodeId?.replace(CHAIN_NODE_SUFFIX, ``) : undefined
  const stoppedNode = stoppedNodeId ? nodes.find((node) => node.id === stoppedNodeId) : undefined
  const showStoppedStep = useCallback(() => {
    if (!stoppedNodeId) return
    setNodes((current) => current.map((node) => ({ ...node, selected: node.id === stoppedNodeId })))
    fitView({ nodes: [{ id: stoppedNodeId }], duration: 300, maxZoom: 1 })
  }, [stoppedNodeId, setNodes, fitView])

  const runStatus = useMemo(() => {
    if (!watchedRunId) return stoppedNodeId ? new Map([[stoppedNodeId, `failed`]]) : EMPTY_RUN_STATUS
    const events = runEventsResult.data?.workflowRunEvents.hits ?? []
    const map = deriveRunNodeStatus(events)
    // The trigger's canvas id never appears in events (it's pruned from the engine graph) — light it
    // from the enrollment event so the run visibly starts at the top.
    const triggerNode = nodes.find((node) => node.type === `trigger`)
    if (triggerNode && hasEnrollment(events)) map.set(triggerNode.id, `completed`)
    return map
  }, [watchedRunId, runEventsResult.data, nodes, stoppedNodeId])

  // Positions are managed, not hand-placed: re-flow only when the STRUCTURE changes. Keyed on a
  // structural signature (positions excluded) to avoid a layout↔position feedback loop.
  const structureKey = useMemo(
    () =>
      `${nodes
        .map((node) => `${node.id}:${node.type}`)
        .sort()
        .join(`|`)}::${edges
        .map((edge) => `${edge.source}>${edge.sourceHandle ?? ``}>${edge.target}`)
        .sort()
        .join(`|`)}`,
    [nodes, edges],
  )
  const hasFitOnce = useRef(false)
  useEffect(() => {
    setNodes((current) => layoutFlow(current, edgesRef.current))
    // 120ms, not 0: at 0 the fit races the trigger modal's unmount and measures stale bounds. The
    // FIRST fit is instant — a glide right after load moves nodes under the user's first click.
    const duration = hasFitOnce.current ? 300 : 0
    const timer = window.setTimeout(() => {
      fitView({ padding: 0.2, duration })
      hasFitOnce.current = true
    }, 120)
    return () => window.clearTimeout(timer)
  }, [structureKey, setNodes, fitView])

  // "+" at an open end: pick a block; it's appended and auto-connected, then the layout effect re-flows.
  const appendFromNode = useCallback(
    ({ nodeId, sourceHandle, screen }: AppendTarget) => {
      const from = nodes.find((node) => node.id === nodeId)
      setPicker({
        screen,
        onTaskPath:
          (from?.type === `createTask` && sourceHandle === TASK_PATH_HANDLE) || isOnTaskPath(nodeId, nodes, edges),
        onPick: (item) => {
          const source = nodes.find((node) => node.id === nodeId)
          if (!source) return
          takeSnapshot()
          const id = ObjectId()
          // Born SELECTED, so its inspector opens immediately — configuring the step you just
          // added is always the next action, and the post-add layout animation was eating the
          // click that used to be required (nodes move under the cursor → ReactFlow reads a drag).
          const newNode = {
            id,
            type: item.kind,
            position: { x: source.position.x, y: source.position.y + 180 },
            data: item.defaults,
            selected: true,
          } as WorkflowNode
          setNodes((nds) => nds.map((node) => ({ ...node, selected: false }) as WorkflowNode).concat(newNode))
          setEdges((eds) =>
            addEdge(
              {
                id: ObjectId(),
                source: nodeId,
                sourceHandle: sourceHandle ?? null,
                target: id,
                type: `connector`,
                animated: true,
              },
              eds,
            ),
          )
          setPicker(null)
        },
      })
    },
    [nodes, edges, takeSnapshot, setNodes, setEdges],
  )

  // Insert a step on an existing edge: the new node fills the target's slot, and the target plus
  // everything downstream of it is pushed down to make room.
  const insertOnEdge = useCallback(
    (edge: InsertEdgeTarget, screen: { x: number; y: number }) => {
      const flowPos = screenToFlowPosition(screen)
      setPicker({
        screen,
        onTaskPath: isOnTaskPath(edge.target, nodes, edges),
        onPick: (item) => {
          takeSnapshot()
          const id = ObjectId()
          const targetNode = nodes.find((node) => node.id === edge.target)
          const position = targetNode
            ? { ...targetNode.position }
            : { x: snap(flowPos.x - NODE_HALF_WIDTH), y: snap(flowPos.y) }
          const downstream = collectDownstream(edge.target, edges)
          // Born selected — same reasoning as appendFromNode.
          const newNode = { id, type: item.kind, position, data: item.defaults, selected: true } as WorkflowNode

          setNodes((nds) =>
            nds
              .map((node) => {
                const shifted = downstream.has(node.id)
                  ? { ...node, position: { x: node.position.x, y: node.position.y + INSERT_SHIFT } }
                  : node
                return { ...shifted, selected: false } as WorkflowNode
              })
              .concat(newNode),
          )
          setEdges((eds) => [
            ...eds.filter((e) => e.id !== edge.id),
            {
              id: ObjectId(),
              source: edge.source,
              sourceHandle: edge.sourceHandle ?? undefined,
              target: id,
              type: `connector`,
              animated: true,
            },
            { id: ObjectId(), source: id, target: edge.target, type: `connector`, animated: true },
          ])
          setPicker(null)
        },
      })
    },
    [nodes, edges, screenToFlowPosition, takeSnapshot, setNodes, setEdges],
  )

  const breakLink = useCallback(
    (edgeId: string) => {
      takeSnapshot()
      setEdges((eds) => eds.filter((e) => e.id !== edgeId))
    },
    [takeSnapshot, setEdges],
  )

  const updateNodeData = useCallback(
    (id: string, patch: Record<string, unknown>) =>
      setNodes((nds) =>
        nds.map((node) => (node.id === id ? ({ ...node, data: { ...node.data, ...patch } } as WorkflowNode) : node)),
      ),
    [setNodes],
  )

  const closeInspector = useCallback(
    () => setNodes((nds) => nds.map((node) => (node.selected ? { ...node, selected: false } : node))),
    [setNodes],
  )

  // Send times are a single workflow-level setting shared by every SMS step.
  const updateSendTimes = useCallback(
    (patch: Partial<SendTimesSettings>) =>
      setSettings((current) => ({ ...current, sendTimes: { ...current.sendTimes, ...patch } })),
    [],
  )
  const updateCategory = useCallback(
    (category: MessageCategory) => setSettings((current) => ({ ...current, category })),
    [],
  )

  // Auto-connect: deleting a step bridges its parent(s) to its next step so the chain never breaks
  // (there's no manual reconnect). The layout effect re-flows afterwards.
  const deleteNode = useCallback(
    (id: string) => {
      takeSnapshot()
      const bridges = bridgeEdges(id, edges).map((bridge) => ({
        id: ObjectId(),
        source: bridge.source,
        sourceHandle: bridge.sourceHandle,
        target: bridge.target,
        type: `connector`,
        animated: true,
      }))
      setEdges((eds) => [...eds.filter((edge) => edge.source !== id && edge.target !== id), ...bridges])
      setNodes((nds) => nds.filter((node) => node.id !== id))
    },
    [edges, takeSnapshot, setEdges, setNodes],
  )

  const duplicateNode = useCallback(
    (id: string) => {
      const original = nodes.find((node) => node.id === id)
      if (!original) return
      takeSnapshot()
      const copy = { ...original, id: ObjectId(), selected: true, data: { ...original.data } } as WorkflowNode
      setNodes((nds) => nds.map((node) => ({ ...node, selected: false }) as WorkflowNode).concat(copy))
      setEdges((eds) => duplicateEdges(original, copy.id, eds))
    },
    [nodes, takeSnapshot, setNodes, setEdges],
  )

  const [movingId, setMovingId] = useState<string | null>(null)
  const movingNode = movingId ? nodes.find((node) => node.id === movingId) : undefined
  const moving = useMemo(() => (movingNode ? moveScope(movingNode, edges) : null), [movingNode, edges])
  const cancelMove = useCallback(() => setMovingId(null), [])
  useEscapeListener(cancelMove, moving !== null)
  const dropMove = useCallback(
    (drop: MoveDrop) => {
      if (!movingNode) return
      takeSnapshot()
      setEdges((eds) => moveNode(movingNode, drop, eds))
      setMovingId(null)
    },
    [movingNode, takeSnapshot, setEdges],
  )

  const actions = useMemo(
    () => ({
      deleteNode,
      duplicateNode,
      startMove: setMovingId,
      dropMove,
      moving,
      appendFromNode,
      insertOnEdge,
      breakLink,
      takeSnapshot,
      issues,
    }),
    [deleteNode, duplicateNode, dropMove, moving, appendFromNode, insertOnEdge, breakLink, takeSnapshot, issues],
  )

  // The compiled trigger as the ENGINE sees it, for the "Why didn't it fire?" explainer — same
  // serializer as save, so the diagnosis and the engine always agree on what the filters are.
  const compiledTrigger = useMemo(
    () => serializeWorkflow({ locationId, name, triggerStartDate, nodes, edges, settings, copy }).input?.trigger,
    [locationId, name, triggerStartDate, nodes, edges, settings, copy],
  )
  // Who going live enrolls now: what happened, or came due, since the backfill day.
  const dateTrigger = !!triggerData && isDateAnchored(triggerData.kind)
  const goLivePreviewOn =
    !active &&
    !!locationId &&
    !!triggerData &&
    triggerData.kind !== `manual` &&
    backfill &&
    (preflightOpen || backfillListOpen || selectedNode?.type === `trigger`)
  const goLiveLookback = useTriggerLookback({
    open: goLivePreviewOn,
    trigger: compiledTrigger,
    locationId,
    range: { since: triggerStartDate },
    workflowId: remoteId,
    targetPipelineId: nodes.find((node): node is AddToPipelineFlowNode => node.type === `addToPipeline`)?.data
      .pipelineId,
  })
  const backfillControl: BackfillControl = {
    on: backfill,
    onChange: (on) => {
      setBackfill(on)
      // The label reads as a day, so the count starts at that day's beginning.
      if (on && !active) setTriggerStartDate(new Date(new Date(triggerStartDate).setHours(0, 0, 0, 0)).toISOString())
      // Off on a live workflow means "keep what it has": a date moved earlier must not replay on save.
      if (!on && active) setTriggerStartDate(savedStart.current)
    },
    live: active,
    preview: goLivePreviewOn
      ? {
          status: goLiveLookback.status,
          total: goLiveLookback.total,
          loadedAt: goLiveLookback.loadedAt,
          onView: () => setBackfillListOpen(true),
        }
      : undefined,
  }
  const goLive: GoLiveEnrollment =
    !triggerData || triggerData.kind === `manual`
      ? { kind: `manual` }
      : !backfill
        ? { kind: `fresh` }
        : goLivePreviewOn
          ? {
              kind: `preview`,
              since: triggerStartDate,
              dateTrigger,
              status: goLiveLookback.status,
              total: goLiveLookback.total,
              loadedAt: goLiveLookback.loadedAt,
              onView: () => setBackfillListOpen(true),
            }
          : { kind: `unknown` }
  const triggerSpec = useMemo((): TriggerSpec | undefined => {
    const compiled = compiledTrigger
    const triggerNode = nodes.find((node): node is TriggerFlowNode => node.type === `trigger`)
    if (!compiled || !triggerNode) return undefined
    const source = engineEventSource(String(compiled.event))
    if (!source) return undefined
    return {
      eventType: source.type,
      eventAction: source.action,
      filters: (compiled.filters ?? []) as TriggerSpec[`filters`],
      stateFilters: compiled.stateFilters as TriggerSpec[`stateFilters`],
      filterLogic: compiled.filterLogic === `or` ? `or` : `and`,
      contactScoped: triggerDef(triggerNode.data.kind)?.object === `contact`,
      locationId,
    }
  }, [compiledTrigger, nodes, locationId])

  // The local copy holds drafts the server can't take yet and saves that didn't reach it, so every
  // path that changes what's saved — Save, activation, and turning one off — writes the whole doc
  // through here.
  const persistLocal = useCallback(
    ({
      id,
      active: docActive,
      remoteId: rid,
      start = triggerStartDate,
      unsynced,
      wentLive: docWentLive = wentLive,
    }: {
      id: string
      active: boolean
      remoteId?: string
      start?: string
      unsynced?: boolean
      wentLive?: boolean
    }) => {
      const triggerNode = nodes.find((node): node is TriggerFlowNode => node.type === `trigger`)
      saveWorkflow(userId, {
        id,
        name,
        locationId,
        workspaceId,
        active: docActive,
        wentLive: docWentLive,
        trigger: triggerNode ? triggerLabel(triggerNode.data.kind) : `No trigger`,
        triggerStartDate: start,
        backfill,
        nodes,
        edges,
        settings,
        remoteId: rid,
        unsynced,
        updatedAt: Date.now(),
      })
    },
    [userId, workspaceId, name, locationId, triggerStartDate, backfill, wentLive, nodes, edges, settings],
  )

  const handleSave = useCallback(
    // `activeOverride` lets the activation preflight activate-and-save in ONE motion: calling
    // setActive then save in the same tick would hand save a stale closure. The typeof guard
    // matters because the toolbar passes this straight to onClick, where the first arg is a
    // MouseEvent, not a boolean.
    async (activeOverride?: unknown) => {
      const effectiveActive = typeof activeOverride === `boolean` ? activeOverride : active
      // SMS consent confirmation: asked BEFORE anything is written, so backing out leaves the
      // workflow exactly as it was. Whenever this save makes a texting workflow live, whatever its
      // trigger (the API only refuses form-triggered ones for now; see smsConsent.ts). A draft is
      // never asked, and neither is a workflow that sends no texts.
      const consentFocus = { returnFocus: () => document.getElementById(`wf-active`) }
      if (effectiveActive && !savedActive && sendsSms(nodes) && !(await ensureSmsConsent(consentFocus))) {
        setActive(savedActive)
        return
      }
      const localId = workflowId ?? ObjectId()
      // With backfill off, the workflow starts the moment it goes live.
      const start = effectiveActive && !savedActive && !backfill ? new Date().toISOString() : triggerStartDate
      const nextWentLive = wentLive || effectiveActive
      // Unsynced until the server accepts it: adoptServerId clears the flag on success.
      const persist = (rid?: string) =>
        persistLocal({
          id: localId,
          active: effectiveActive,
          remoteId: rid,
          start,
          unsynced: true,
          wentLive: nextWentLive,
        })

      // The local doc is written first regardless of whether the graph can sync to the engine.
      const { input, blocked, gaps } = serializeWorkflow({
        locationId,
        locationHasBusinessHours: businessHoursReady,
        name,
        triggerStartDate: start,
        backfill,
        wentLive: nextWentLive,
        nodes,
        edges,
        settings,
        copy,
      })
      persist(remoteId)
      setWentLive(nextWentLive)
      setTriggerStartDate(start)
      savedStart.current = start
      setSavedSig(workflowSignature(name, start, backfill, nodes, edges, settings))
      setSavedActive(effectiveActive)
      // Pending gaps never block a draft's save, but a live workflow's save IS the engine's copy:
      // pushing a keyword wait there answers any reply, so the live version stays as it was.
      const holdLive = effectiveActive && pendingEngineGaps(nodes).length > 0
      const serverInput = blocked || holdLive ? undefined : input

      if (blocked || !input) {
        // Two different reasons land here, and telling someone their steps are unsupported when they
        // simply have not picked a location sends them hunting through the canvas for a fault that
        // isn't there.
        const missingLocation = gaps.find((gap) => gap.code === `unsupported_settings` && !locationId)
        notify(
          missingLocation
            ? `Saved as a local draft. ${missingLocation.message}`
            : `Saved as a local draft. Some steps aren't supported by the engine yet, so it can't be set live until they're added.`,
        )
      } else if (holdLive) {
        notify(
          `Saved as a local draft. The live workflow is unchanged, because this version uses steps the workflow engine can't run yet.`,
        )
      }

      // Category is an admin claim at the engine (api #581/#588: every workflow sends as marketing
      // until a super marks it transactional via the ops schema). For supers, the picker ACTS on
      // save; for everyone else it stays the honest label the panel explains. Absent server-side
      // means marketing, so only a real disagreement makes the call.
      const syncCategory = async (remoteWorkflowId: string) => {
        const plan = categorySyncPlan({
          canSetCategory: !!authStore.isSuper,
          settings,
          serverCategory: syncedCategory.current,
        })
        if (!plan.apply) return
        const { error } = await setRemoteWorkflowCategory(remoteWorkflowId, plan.category)
        // A failed sync must not read as a clean save: the workflow is live under the OLD category.
        if (error) notify(`Saved, but the texting category didn't sync — it still sends as before: ${error}`)
        else syncedCategory.current = plan.category
      }

      // The pre-check can miss (a second tab, a stale read); the server's refusal then opens the
      // same modal. If the user backs out THERE, the local copy already says "on": put the switch
      // and the saved state back where the server is, as handleDeactivate's failure does.
      const consentRefused = (result: { error?: string }) => {
        if (!isSmsConsentRefusal(result.error)) return false
        setActive(savedActive)
        setSavedActive(savedActive)
        persistLocal({ id: localId, active: savedActive, remoteId })
        return true
      }

      // The id this workflow is addressed by from here on. It stays the local one only while the
      // server has never seen the workflow (a draft the engine can't take yet); the moment there is
      // a server id, that is the workflow, and the URL and the local copy both move to it.
      let addressId = localId

      if (serverInput && remoteId) {
        const { locationId: nextLocationId, ...updatable } = serverInput
        const locationChanged = nextLocationId !== syncedLocationId.current
        const updated = await runSmsGated(
          () =>
            updateRemoteWorkflow(remoteId, {
              ...updatable,
              ...(locationChanged ? { locationId: nextLocationId } : {}),
              active: effectiveActive,
            }),
          consentFocus,
        )
        if (consentRefused(updated)) return
        if (updated.error) notify(`Saved locally, but syncing to the server failed: ${updated.error}`)
        else {
          if (locationChanged) syncedLocationId.current = nextLocationId
          await syncCategory(remoteId)
          // Heals a workflow saved by the older builder, which left the URL (and the local copy)
          // keyed by the id it minted rather than the one the server created.
          adoptServerId(userId, localId, remoteId)
          addressId = remoteId
          notifySuccess(`Workflow saved`)
        }
      } else if (serverInput) {
        const { data, error } = await createRemoteWorkflow(serverInput)
        if (error || !data) {
          notify(`Saved locally, but syncing to the server failed${error ? `: ${error}` : ``}`)
        } else {
          let synced = data
          // createWorkflow always makes an inactive workflow; mirror a local "active" via a follow-up.
          if (effectiveActive) {
            const activated = await runSmsGated(() => updateRemoteWorkflow(data.id, { active: true }), consentFocus)
            // Created, but still a draft on the server: the rest of the save (id adoption, URL)
            // must still happen, so this does not return.
            if (!consentRefused(activated) && activated.error)
              notify(`Saved, but couldn't set it live: ${activated.error}`)
            else if (activated.data) synced = activated.data
          }
          setRemoteId(synced.id)
          syncedLocationId.current = serverInput.locationId
          syncedCategory.current = synced.category ?? null
          await syncCategory(synced.id)
          adoptServerId(userId, localId, synced.id)
          addressId = synced.id
          notifySuccess(`Workflow saved`)
        }
      }

      // Move to the saved workflow's edit URL so reopening (or sharing) that URL loads it back. A
      // save that reached the server addresses the server's id; a local-only draft keeps the local
      // one. Replacing rather than pushing when the URL already named this workflow keeps Back
      // pointing where the user came from.
      if (addressId !== workflowId) navigate(`/workflows/${addressId}`, { replace: !!workflowId })
    },
    [
      workflowId,
      remoteId,
      name,
      locationId,
      businessHoursReady,
      triggerStartDate,
      backfill,
      active,
      savedActive,
      wentLive,
      nodes,
      edges,
      settings,
      copy,
      navigate,
      userId,
      authStore,
      persistLocal,
      ensureSmsConsent,
      runSmsGated,
    ],
  )

  /**
   * Turning a workflow OFF persists on the spot — no Save, no confirmation (TRK-5695).
   *
   * The reason someone reaches for this switch in a hurry is to STOP texts going to patients, and it
   * used to be editor-local: the header flipped to Draft, the button went to Save, and the workflow
   * kept running on the server until a second, separate click. Off is also the safe direction, so it
   * asks nothing before acting.
   *
   * This deliberately does NOT go through handleSave. A save serializes the whole canvas and skips
   * the server entirely when the graph is blocked (an unsupported step, a cleared location), which is
   * exactly the state a half-edited live workflow can be in — the one case where "off" must still
   * reach the engine. Sending only `{ active: false }` can't be blocked by local edits, and leaves
   * any unsaved canvas work pending as it was.
   */
  const handleDeactivate = useCallback(async () => {
    setActive(false)
    const id = workflowId ?? remoteId
    if (id) persistLocal({ id, active: false, remoteId })
    if (!remoteId) {
      // Local-only draft: it was never synced, so nothing is running to stop.
      setSavedActive(false)
      return
    }
    const { error } = await updateRemoteWorkflow(remoteId, { active: false })
    if (error) {
      // A stop that didn't stop must never read as off. Put the switch back where the server is.
      setActive(true)
      if (id) persistLocal({ id, active: true, remoteId })
      notify(`Couldn't turn off this workflow. It's still running: ${error}`)
      return
    }
    setSavedActive(false)
    setPaused(false)
    notifySuccess(`Workflow turned off`)
  }, [workflowId, remoteId, persistLocal])

  // A deliberate pause holds every patient at their current step and blocks new enrollment without
  // ending any run (TRK-6204) — distinct from turning the workflow off, which ends every run. It
  // acts on the server immediately and does not touch the local draft or go through Save.
  const handleTogglePause = useCallback(
    async (next: boolean) => {
      if (!remoteId || pausing) return
      setPausing(true)
      setPaused(next)
      const { error } = await updateRemoteWorkflow(
        remoteId,
        next ? { paused: true, pausedReason: `paused_by_staff` } : { paused: false },
      )
      setPausing(false)
      if (error) {
        setPaused(!next)
        notify(next ? `Couldn't pause this workflow: ${error}` : `Couldn't unpause this workflow: ${error}`)
        return
      }
      notifySuccess(next ? `Workflow paused` : `Workflow unpaused`)
    },
    [remoteId, pausing],
  )

  // Trigger chosen in the modal becomes the root node. On a blank canvas that's the whole graph;
  // on a HEADLESS one (the trigger was deleted) the new trigger grafts onto the existing steps —
  // replacing them here would destroy the user's work one click after they deleted a trigger
  // (08-24 audit: that was exactly the old behavior).
  const handleSelectTrigger = useCallback(
    (choice: TriggerChoice) => {
      takeSnapshot()
      const triggerNode = {
        id: ObjectId(),
        type: `trigger`,
        position: { x: 280, y: 40 },
        // Preset property prefill (conditions / source / policies) merges in and stays editable.
        data: {
          label: choice.label,
          kind: choice.kind,
          summary: choice.summary,
          reenrollment: `ignore`,
          ...choice.data,
        },
        // Born selected: reviewing the trigger you just chose is always the next action.
        selected: true,
      } as WorkflowNode
      // The old head: the topmost step nothing points at. The new trigger connects to it.
      const head = nodes.find((node) => node.type !== `trigger` && !edges.some((edge) => edge.target === node.id))
      setNodes((current) => [
        triggerNode,
        ...current
          .filter((node) => node.type !== `trigger`)
          .map((node) => ({ ...node, selected: false }) as WorkflowNode),
      ])
      setEdges((current) =>
        head
          ? addEdge(
              {
                id: ObjectId(),
                source: triggerNode.id,
                sourceHandle: null,
                target: head.id,
                type: `connector`,
                animated: true,
              },
              current,
            )
          : [],
      )
      setPickingTrigger(false)
    },
    [takeSnapshot, setNodes, setEdges, nodes, edges],
  )

  // Undo / redo keyboard shortcuts (ignored while typing in a field).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== `z`) return
      if (isTypingTarget(event.target)) return
      event.preventDefault()
      if (event.shiftKey) redo()
      else undo()
    }
    window.addEventListener(`keydown`, onKeyDown)
    return () => window.removeEventListener(`keydown`, onKeyDown)
  }, [undo, redo])

  if (notFound) {
    // A plain "Not found" is what this panel already says; anything else (a network failure, a
    // permission error) would be misdiagnosed as a missing workflow without the server's words.
    const detail =
      remoteResult.error && !/not\s*found/i.test(remoteResult.error.message)
        ? remoteResult.error.message.replace(/^\[GraphQL\]\s*/, ``)
        : undefined
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center">
        <span className="flex size-10 items-center justify-center rounded-xl bg-surface-muted text-subtle">
          <GitForkIcon size={22} weight="bold" />
        </span>
        <p className="mt-3 text-sm font-medium text-default">Workflow not found</p>
        <p className="mt-1 max-w-sm text-sm text-subtle">
          {detail ?? `This link doesn't point at a workflow. It may have been deleted, or saved under another account.`}
        </p>
        <Button variant="primary" size="sm" className="mt-4" onClick={() => navigate(`/workflows`)}>
          Back to workflows
        </Button>
      </div>
    )
  }

  return (
    <RunStatusContext.Provider value={runStatus}>
      <WorkflowActionsProvider value={actions}>
        <div className="flex h-full flex-col">
          <WorkflowToolbar
            name={name}
            onNameChange={setName}
            active={active}
            paused={paused}
            pausing={pausing}
            wentLive={wentLive}
            hold={hold}
            // Draft → Active goes through the preflight (UX audit B2), which activates AND saves.
            // Active → Draft asks nothing and persists immediately (TRK-5695): both directions take
            // effect on the click, neither waits on Save.
            onActiveChange={(next) => {
              if (!next) handleDeactivate()
              else if (showConsentBar) setConsentNudge((count) => count + 1)
              else setPreflightOpen(true)
            }}
            onTogglePause={handleTogglePause}
            nodeCount={nodes.length}
            issueCount={issueCount}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={undo}
            onRedo={redo}
            onSave={handleSave}
            onOpenRuns={() => setRunsOpen(true)}
            onOpenSettings={() => {
              // The trigger picker holds the panel on a new workflow; settings were asked for.
              setPickingTrigger(false)
              setSettingsOpen(true)
            }}
            onOpenMatches={matchesAvailable ? () => setMatchesOpen(true) : undefined}
            wontRunYet={wontRunYet}
            dirty={dirty}
            locations={locations}
            locationId={locationId}
            onLocationChange={setLocationId}
            textingPolicy={
              // Gated on a texting step, the same rule the activation preflight uses for its consent line.
              nodes.some((node) => node.type === `sendSms` || node.type === `aiAgentSms`)
                ? {
                    ...effectiveCategory({
                      canSetCategory: authStore.isSuper,
                      settings,
                      serverCategory: syncedCategory.current,
                    }),
                    onOpen: () => setSettingsOpen(true),
                  }
                : undefined
            }
          />
          {stopped && hold && (
            <WorkflowStoppedBanner
              hold={hold}
              reason={stopped.pausedReason}
              stepLabel={stoppedNode?.data.label}
              stoppedAt={stopped.pausedDate}
              heldCount={stoppedResult.data?.workflowRuns.total}
              locationId={locationId}
              onShowStep={stoppedNode ? showStoppedStep : undefined}
            />
          )}
          <div className="relative flex min-h-0 flex-1">
            <div className="relative flex min-w-0 flex-1">
              <WorkflowCanvas
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onChooseTrigger={() => setPickingTrigger(true)}
                pickingTrigger={pickingTrigger}
                locations={locations}
                locationId={locationId}
                onLocationChange={setLocationId}
              />
              {showConsentBar && (
                <SmsConsentBar
                  nudge={consentNudge}
                  onOpen={async () => {
                    setConsentNudge(0)
                    const confirmed = await ensureSmsConsent({
                      returnFocus: () => document.getElementById(`wf-active`),
                    })
                    // Confirming here only answers the question. Turning it on still goes through the
                    // usual preflight, which shows what the workflow will do as it stands now.
                    if (confirmed && issueCount === 0) setPreflightOpen(true)
                  }}
                />
              )}
              {/* Watch-mode banner: watching deliberately persists after the Runs panel closes (the
                  point is seeing the canvas light up), so the canvas itself must say it's live and
                  offer the exit. */}
              {movingNode && (
                <div className="pointer-events-none absolute inset-x-0 top-4 z-30 flex justify-center">
                  <div className="pointer-events-auto flex items-center gap-2.5 rounded-full bg-inverse py-1.5 pr-1.5 pl-3.5 shadow-lg ring-1 ring-strong animate-in fade-in slide-in-from-top-2">
                    <span className="text-xs font-medium text-inverse">
                      Moving {movingNode.data.label}
                      {(moving?.travelling.size ?? 0) > 1 && ` and the steps under it`}
                    </span>
                    <span className="hidden text-[11px] text-inverse/60 md:block">pick a spot</span>
                    <Button variant="white" size="xs" shape="pill" onClick={cancelMove}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {watchedRunId && (
                <div className="pointer-events-none absolute inset-x-0 top-4 z-30 flex justify-center">
                  <div className="pointer-events-auto flex items-center gap-2.5 rounded-full bg-inverse py-1.5 pr-1.5 pl-3.5 shadow-lg ring-1 ring-strong animate-in fade-in slide-in-from-top-2">
                    <span className="relative flex size-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                      <span className="relative inline-flex size-2 rounded-full bg-blue-500" />
                    </span>
                    <span className="text-xs font-medium text-inverse">Watching run</span>
                    <span className="hidden text-[11px] text-inverse/60 md:block">nodes light up as it executes</span>
                    <Button variant="white" size="xs" shape="pill" onClick={() => setWatchedRunId(null)}>
                      Stop
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {pickingTrigger ? (
              <TriggerPicker onSelect={handleSelectTrigger} onClose={() => setPickingTrigger(false)} />
            ) : settingsOpen ? (
              <WorkflowSettingsPanel
                settings={settings}
                onSendTimesChange={updateSendTimes}
                onCategoryChange={updateCategory}
                canSetCategory={authStore.isSuper}
                onClose={() => setSettingsOpen(false)}
              />
            ) : selectedNode ? (
              <NodeInspector
                onChangeTrigger={() => setPickingTrigger(true)}
                node={selectedNode}
                onChange={updateNodeData}
                onClose={closeInspector}
                settings={settings}
                onEditSettings={() => setSettingsOpen(true)}
                availableFields={availableFields}
                contact={contact}
                activity={activity}
                issues={nodeIssuesFor(issues, selectedNode.id)}
                active={active}
                saved={!!remoteId}
                dirty={dirty}
                nodes={nodes}
                edges={edges}
                preview={preview}
                triggerStartDate={triggerStartDate}
                onTriggerStartDateChange={setTriggerStartDate}
                backfill={backfillControl}
              />
            ) : null}
          </div>
        </div>
        {picker && (
          <BlockPicker
            screen={picker.screen}
            onTaskPath={picker.onTaskPath}
            onPick={picker.onPick}
            onClose={() => setPicker(null)}
          />
        )}
        {smsConsentModal}
        {preflightOpen && (
          <ActivationPreflight
            name={name}
            nodes={nodes}
            settings={settings}
            caps={throttle.settings}
            canSetCategory={authStore.isSuper}
            serverCategory={syncedCategory.current}
            onOpenCaps={() => setCapsOpen(true)}
            goLive={goLive}
            onConfirm={() => {
              setPreflightOpen(false)
              setActive(true)
              // One motion: a confirmed preflight saves immediately so "active" is never a stale
              // local flag waiting on a separate Save (the override dodges the closure's old value).
              handleSave(true)
            }}
            onClose={() => setPreflightOpen(false)}
          />
        )}
        {/* After the go-live check, so "See who" opens on top of it. */}
        {backfillListOpen && goLivePreviewOn && (
          <MatchesPanel
            open
            mode="backfill"
            since={triggerStartDate}
            dateTrigger={dateTrigger}
            onClose={() => setBackfillListOpen(false)}
            lookback={goLiveLookback}
            sourceNoun={compiledTrigger ? engineEventNoun(String(compiledTrigger.event)) : `trigger`}
            timeZone={locations.find((location) => location.id === locationId)?.timezone}
          />
        )}
        {capsOpen && (
          <GlobalSettingsModal
            initial={throttle.settings}
            onClose={() => setCapsOpen(false)}
            onSave={async (next) => {
              if (!throttle.workspaceId) return `No workspace selected.`
              const result = await workspaceStore.update(throttle.workspaceId, workspaceInputFromSettings(next))
              if (result.error) {
                logError(`Failed to save workspace throttle settings`, result.error)
                return result.error.message.replace(/^\[GraphQL\]\s*/, ``)
              }
              throttle.setSettings(next)
              setCapsOpen(false)
              notifySuccess(`Automation settings saved`)
              return null
            }}
          />
        )}
        <RunsPanel
          workflowId={remoteId}
          locationId={locationId}
          locationTimezone={locations.find((location) => location.id === locationId)?.timezone}
          active={active}
          open={runsOpen}
          onClose={() => setRunsOpen(false)}
          onWatchRun={setWatchedRunId}
          triggerSpec={triggerSpec}
          triggerStartDate={triggerStartDate}
          nodes={nodes}
        />
        {matchesAvailable && matchesOpen && (
          <WorkflowMatches
            onClose={() => setMatchesOpen(false)}
            trigger={compiledTrigger}
            locationId={locationId}
            workflowId={remoteId}
            nodes={nodes}
            timeZone={locations.find((location) => location.id === locationId)?.timezone}
          />
        )}
      </WorkflowActionsProvider>
    </RunStatusContext.Provider>
  )
})

/** Canvas-style workflow builder. ReactFlowProvider must wrap the inner component so
 *  hooks like useReactFlow / screenToFlowPosition work. */
export function WorkflowBuilderPage(props: WorkflowBuilderPageProps) {
  return (
    <ReactFlowProvider>
      <WorkflowBuilderInner {...props} />
    </ReactFlowProvider>
  )
}
